<script setup>
import { Head, usePage } from '@inertiajs/vue3';
import { computed } from 'vue';
import FrontLayout from '@/Layouts/FrontLayout.vue';
import PageHero from '@/Components/Front/PageHero.vue';

const page = usePage();

const props = defineProps({
    setting: Object,
    title: String,
    meta_description: String,
    meta_keywords: String,
    og_title: String,
    og_description: String,
    og_image: String,
    og_url: String,
    twitter_title: String,
    twitter_description: String,
    twitter_image: String,
    canonical_url: String,
    selectedArea: String,
});

const getLocation = () => {
    if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
            const lat = position.coords.latitude;
            const long = position.coords.longitude;
            window.open(`https://www.google.com/maps/dir/?api=1&destination=${props.setting?.setting_alamat ? encodeURIComponent(props.setting.setting_alamat) : '-8.2191,114.3691'}&origin=${lat},${long}`, '_blank');
        }, (error) => {
            alert("Gagal mendapatkan lokasi. Pastikan GPS aktif.");
        });
    } else {
        alert("Geolocation tidak didukung browser ini.");
    }
};

const serviceCoverage = [
    { title: 'Banyuwangi Kota', desc: 'Melayani area pusat kota Banyuwangi dan sekitarnya', icon: 'fa-map-marker' },
    { title: 'Kawasan Perkantoran', desc: 'Melayani transportasi ke gedung perkantoran dan bisnis', icon: 'fa-building' },
    { title: 'Bandara & Pelabuhan', desc: 'Antar jemput bandara dan pelabuhan Ketapang', icon: 'fa-plane' },
    { title: 'Destinasi Wisata', desc: 'Kawah Ijen, Pulau Merah, Pantai, dan tempat wisata lainnya', icon: 'fa-tree' },
    { title: 'Lokasi Custom', desc: 'Hubungi kami untuk kebutuhan transportasi ke lokasi khusus', icon: 'fa-globe' },
];

// JSON-LD Schema
const jsonLd = computed(() => {
    const appUrl = page.props.app_url || window.location.origin;
    const currentUrl = props.canonical_url || window.location.href;
    const setting = props.setting || {};
    const logoUrl = setting.setting_logo ? `/storage/logo/${setting.setting_logo}` : '/front/img/logo.png';

    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "ContactPage",
                "name": props.title || 'Hubungi Kami - Gotransport Banyuwangi',
                "description": props.meta_description,
                "url": currentUrl,
                "mainEntity": {
                    "@type": "LocalBusiness",
                    "name": setting.setting_title || 'Gotransport Banyuwangi',
                    "image": props.og_image || `${appUrl}${logoUrl}`,
                    "telephone": setting.setting_no_telp || setting.setting_no_wa,
                    "email": setting.setting_email,
                    "address": {
                        "@type": "PostalAddress",
                        "streetAddress": setting.setting_alamat || 'Banyuwangi',
                        "addressLocality": "Banyuwangi",
                        "addressRegion": "Jawa Timur",
                        "postalCode": "68400",
                        "addressCountry": "ID"
                    },
                    "url": appUrl
                }
            },
            {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { 
                        "@type": "ListItem", 
                        "position": 1, 
                        "name": "Beranda", 
                        "item": appUrl 
                    },
                    { 
                        "@type": "ListItem", 
                        "position": 2, 
                        "name": "Kontak", 
                        "item": currentUrl 
                    }
                ]
            }
        ]
    };
});
</script>

<template>
    <FrontLayout 
        :setting="setting"
        :title="title" 
        :metaDescription="meta_description"
        :metaKeywords="meta_keywords"
        :ogTitle="og_title"
        :ogDescription="og_description"
        :ogImage="og_image"
        :ogUrl="og_url"
        :twitterTitle="twitter_title"
        :twitterDescription="twitter_description"
        :twitterImage="twitter_image"
    >
        <Head>
            <link v-if="canonical_url" rel="canonical" :href="canonical_url" />
            <component :is="'script'" type="application/ld+json">{{ JSON.stringify(jsonLd) }}</component>
        </Head>

        <PageHero 
            title="Hubungi Kami"
            subtitle="Sewa mobil Banyuwangi & rental mobil Banyuwangi terpercaya - Gotransport siap melayani Anda"
            :breadcrumbs="[
                { label: 'Beranda', url: '/' },
                { label: 'Kontak' }
            ]"
        />

        <section class="py-12 lg:py-16 bg-white dark:bg-gray-900 min-h-[500px]">
            <div class="container mx-auto px-4 max-w-7xl">
                <!-- Heading -->
                <div class="mb-8">
                     <div class="pl-4 border-l-4 border-orange-600 ml-4 lg:ml-0">
                         <h2 class="text-2xl lg:text-3xl font-semibold text-gray-800 dark:text-white mb-2">Kontak Sewa Mobil Banyuwangi</h2>
                         <p class="text-base text-gray-600 dark:text-gray-400">Hubungi tim Gotransport untuk kebutuhan rental mobil Banyuwangi. Kami siap membantu perjalanan wisata dan bisnis Anda.</p>
                     </div>
                </div>

                <!-- Contact Details -->
                <div class="mb-12">
                     <h3 class="text-xl lg:text-2xl font-semibold text-gray-800 dark:text-white mb-6 relative pb-2 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-10 after:h-0.5 after:bg-orange-600">
                         Detail Kontak
                     </h3>

                     <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                         <div v-if="setting?.setting_alamat" class="py-4">
                             <div class="flex items-start gap-3">
                                 <div class="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 dark:bg-gray-800 flex items-center justify-center text-orange-600 dark:text-orange-400">
                                     <i class="fa fa-map-marker text-xl"></i>
                                 </div>
                                 <div>
                                     <h6 class="font-semibold text-gray-800 dark:text-white mb-1">Alamat Kantor</h6>
                                     <p class="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">{{ setting.setting_alamat }}</p>
                                 </div>
                             </div>
                         </div>
                         
                         <div v-if="setting?.setting_email" class="py-4">
                             <div class="flex items-start gap-3">
                                 <div class="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 dark:bg-gray-800 flex items-center justify-center text-orange-600 dark:text-orange-400">
                                     <i class="fa fa-envelope text-xl"></i>
                                 </div>
                                 <div class="break-all">
                                     <h6 class="font-semibold text-gray-800 dark:text-white mb-1">Email Address</h6>
                                     <a :href="`mailto:${setting.setting_email}`" class="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 transition-colors text-sm">
                                         {{ setting.setting_email }}
                                     </a>
                                 </div>
                             </div>
                         </div>

                         <div v-if="setting?.setting_no_telp" class="py-4">
                             <div class="flex items-start gap-3">
                                 <div class="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 dark:bg-gray-800 flex items-center justify-center text-orange-600 dark:text-orange-400">
                                     <i class="fa fa-phone text-xl"></i>
                                 </div>
                                 <div>
                                     <h6 class="font-semibold text-gray-800 dark:text-white mb-1">Phone Number</h6>
                                     <a :href="`tel:${setting.setting_no_telp}`" class="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 transition-colors text-sm">
                                         {{ setting.setting_no_telp }}
                                     </a>
                                 </div>
                             </div>
                         </div>

                         <div v-if="setting?.setting_no_wa" class="py-4">
                             <div class="flex items-start gap-3">
                                 <div class="flex-shrink-0 w-12 h-12 rounded-full bg-orange-100 dark:bg-gray-800 flex items-center justify-center text-orange-600 dark:text-orange-400">
                                     <i class="fa fa-whatsapp text-xl"></i>
                                 </div>
                                 <div>
                                     <h6 class="font-semibold text-gray-800 dark:text-white mb-1">WhatsApp</h6>
                                     <a :href="`https://wa.me/${setting.setting_no_wa}`" target="_blank" rel="noopener" class="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 transition-colors text-sm">
                                         {{ setting.setting_no_wa }}
                                     </a>
                                 </div>
                             </div>
                         </div>
                     </div>

                     <div class="pt-4 border-t border-gray-100 dark:border-gray-800">
                         <button @click="getLocation" class="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg shadow hover:shadow-md transition-all">
                             <i class="fa fa-crosshairs"></i>
                             <span>Dapatkan Lokasi Saya</span>
                         </button>
                     </div>
                </div>

                <!-- Map -->
                <div class="mb-12">
                     <h3 class="text-xl lg:text-2xl font-semibold text-gray-800 dark:text-white mb-6 relative pb-2 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-10 after:h-0.5 after:bg-orange-600">
                         Lokasi Kantor
                     </h3>
                     <div class="w-full h-[400px] lg:h-[500px] rounded-lg overflow-hidden shadow-lg bg-gray-100 dark:bg-gray-800">
                         <div v-if="setting?.setting_map" class="w-full h-full [&>iframe]:w-full [&>iframe]:h-full [&>iframe]:border-0" v-html="setting.setting_map"></div>
                         <div v-else class="w-full h-full flex items-center justify-center">
                             <div class="text-center text-gray-400 dark:text-gray-500">
                                 <i class="fa fa-map-o text-6xl mb-4"></i>
                                 <p class="text-lg">Peta Lokasi Belum Tersedia</p>
                             </div>
                         </div>
                     </div>
                </div>

                <!-- Services -->
                <div class="mt-12">
                     <h3 class="text-xl lg:text-2xl font-semibold text-gray-800 dark:text-white mb-6 relative pb-2 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-10 after:h-0.5 after:bg-orange-600">
                         Cakupan Layanan Kami
                     </h3>
                     <p class="text-gray-600 dark:text-gray-400 mb-8">Kami melayani berbagai wilayah dengan armada yang siap membantu kebutuhan transportasi Anda</p>
                     
                     <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                         <div v-for="(svc, idx) in serviceCoverage" :key="idx" class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 hover:border-orange-600 dark:hover:border-orange-500 transition-all duration-300">
                             <div class="flex items-start gap-4">
                                 <div class="flex-shrink-0 w-14 h-14 bg-orange-600 flex items-center justify-center text-white">
                                     <i class="fa fa-map-marker text-2xl" :class="svc.icon"></i>
                                 </div>
                                 <div class="flex-grow">
                                     <h4 class="font-semibold text-gray-800 dark:text-white mb-2">{{ svc.title }}</h4>
                                     <p class="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">{{ svc.desc }}</p>
                                 </div>
                             </div>
                         </div>
                     </div>

                     <div class="mt-8 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 flex flex-col md:flex-row items-center gap-4">
                         <div class="w-16 h-16 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center text-orange-600 dark:text-orange-400 flex-shrink-0">
                             <i class="fa fa-info-circle text-3xl"></i>
                         </div>
                         <div>
                             <h4 class="font-semibold text-gray-800 dark:text-white mb-2">Informasi Penting</h4>
                             <p class="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                                Area layanan dapat disesuaikan dengan kebutuhan Anda. Untuk informasi lebih lanjut mengenai cakupan area dan tarif, 
                                silakan hubungi customer service kami melalui WhatsApp atau telepon yang tersedia di atas.
                             </p>
                         </div>
                     </div>
                </div>
            </div>
        </section>
    </FrontLayout>
</template>
